import type { CartDiscountValue } from '@commercetools/platform-sdk';
import type {
  Cart,
  Order,
  DiscountedLineItemPortion,
  LineItem,
  DiscountCodeInfo,
  CartDiscount,
  DiscountCode,
} from '~~/types/commercetools';
import { DISCOUNT_TYPE, DISCOUNT_CATEGORY } from '~~/types/discounts';
import type { DiscountTarget, MappedDiscount, DiscountCategory } from '~~/types/discounts';
import { getLoyaltyLevelNumber } from '~~/helpers/getLoyaltyLevelNumber';
import { MONETARY_COHORT } from '~~/constants/regex';
import { DISCOUNT_FIELDS } from '~~/constants/commerceTools';
import { DISTINCT_PRODUCTS_CART_DISCOUNT_KEYS } from '~~/constants/cartDiscounts';
import { getVolumeDiscountMinimalQuantity } from '~~/helpers/voumeDiscounts/getVolumeDiscountMinimalQuantity';
import { getDistinctProductsMinimalCount } from '~~/helpers/discounts/getDistinctProductsMinimalCount';

export const extractMinimalQuantity = (
  cartDiscount: DiscountedLineItemPortion,
  category: DiscountCategory,
): number | undefined => {

  switch (category) {

    case DISCOUNT_CATEGORY.VOLUME:
      return getVolumeDiscountMinimalQuantity(cartDiscount?.discount) ?? undefined;
    case DISCOUNT_CATEGORY.DISTINCT_PRODUCTS:
      return getDistinctProductsMinimalCount(cartDiscount?.discount) ?? undefined;
    default:
      return undefined;

  }

};

export const extractLoyaltyLevelNumber = (
  cartDiscount: DiscountedLineItemPortion,
  category: DiscountCategory,
): number | undefined => {

  if (category !== DISCOUNT_CATEGORY.LOYALTY || !cartDiscount?.discount?.cartPredicate) {

    return undefined;
  
  }

  const cohortMatch = cartDiscount.discount.cartPredicate.match(MONETARY_COHORT);

  if (cohortMatch?.[1]) {

    return getLoyaltyLevelNumber(cohortMatch[1]);
  
  }

  return undefined;

};

export const getDiscountCategory = (discount: Maybe<CartDiscount>): DiscountCategory => {

  if (!discount) {

    return DISCOUNT_CATEGORY.OTHER;
  
  }

  if (discount.custom?.customFieldsRaw?.some(field =>
    field.name === DISCOUNT_FIELDS.VOLUME_DISCOUNT_GROUP && field.value,
  )) {

    return DISCOUNT_CATEGORY.VOLUME;
  
  }

  if (discount.key && DISTINCT_PRODUCTS_CART_DISCOUNT_KEYS.includes(discount.key)) {

    return DISCOUNT_CATEGORY.DISTINCT_PRODUCTS;

  }

  if (discount.cartPredicate?.startsWith('customer.customerGroupAssignments.customerGroup.key contains')) {

    return DISCOUNT_CATEGORY.LOYALTY;
  
  }

  if (discount.requiresDiscountCode) {

    return DISCOUNT_CATEGORY.PROMO_CODE;
  
  }

  return DISCOUNT_CATEGORY.OTHER;

};

export const mapCartDiscount = (cartDiscount: DiscountedLineItemPortion, quantity = 1): MappedDiscount => {

  const cartDiscountValue = cartDiscount?.discount?.value as CartDiscountValue;
  let discountValue = null;
  const discountType = cartDiscountValue?.type;
  const discountTargetType = (cartDiscount?.discount?.target?.type || null) as DiscountTarget;
  switch (cartDiscountValue?.type) {

    case DISCOUNT_TYPE.Relative:
      discountValue = cartDiscountValue.permyriad / 100;
      break;
    case DISCOUNT_TYPE.GiftLineItem:
      discountValue = null;
      break;
    case DISCOUNT_TYPE.Fixed:
    case DISCOUNT_TYPE.Absolute:
      discountValue = cartDiscountValue.money[0]?.centAmount / 100;
  
  }

  const discountedAmount =
    (cartDiscount.discountedAmount?.centAmount * quantity) / 100;

  const category = getDiscountCategory(cartDiscount?.discount);

  const minimalQuantity = extractMinimalQuantity(cartDiscount, category);
  const loyaltyLevelNumber = extractLoyaltyLevelNumber(cartDiscount, category);

  return {
    id: cartDiscount?.discount?.id || '',
    name: cartDiscount?.discount?.name || '',
    type: discountType,
    target: discountTargetType,
    value: discountValue,
    discountedAmount,
    requiresDiscountCode: !!cartDiscount?.discount?.requiresDiscountCode,
    products: [],
    category,
    minimalQuantity,
    loyaltyLevel: loyaltyLevelNumber
      ? { levelNumber: loyaltyLevelNumber }
      : undefined,
  };

};

/* we don't use the getCartDiscounts to get the first code because
// getCartDiscounts search in cart.lineItems instead of card.discountCodes
There exists only info about the name(which could be diff from the code) and the price */
export const getFirstDiscountCode = (cartOrOrder: Cart | Order): DiscountCode['code'] => {

  return cartOrOrder?.discountCodes?.[0]?.discountCode?.code || '';

};

export const getCartDiscounts = (cart: Cart): MappedDiscount[] => {

  if (!cart) {

    return [];
  
  }
  const shippingDiscounts =
    cart?.shippingInfo?.discountedPrice?.includedDiscounts?.map(
      (shippingDiscount) => mapCartDiscount(shippingDiscount),
    ) || [];

  const cartDiscounts = parseCartDiscounts(cart.lineItems) || [];

  return cartDiscounts.concat(shippingDiscounts);

};

export const getDiscountCode = (discount: DiscountCodeInfo): DiscountCode['code'] => {

  return discount?.discountCode?.code || '';

};

export const findDiscountCodeInCart = (cart: Cart, promoCode: string): DiscountCodeInfo | undefined => {

  return cart?.discountCodes.find(discount => getDiscountCode(discount) === promoCode);

};

/**
 * @param cart The whole cart object
 * @param couponId The coupon ID
 * @returns Find the discount code which only exists in cart.discountCode
 * and not in lineItems as the other discount info
 */
export const getDiscountCodeByCouponId = (cart: Cart, couponId: CartDiscount['id']): DiscountCode['code'] => {

  const findCartDiscountItem = (discount: DiscountCodeInfo) =>
    discount?.discountCode?.cartDiscounts?.find(c => c.id === couponId);

  return cart?.discountCodes?.find(discount => findCartDiscountItem(discount))?.discountCode?.code || '';

};

export const parseCartDiscounts = (lineItems: LineItem[]): MappedDiscount[] => lineItems?.reduce<MappedDiscount[]>(
  (currentCartDiscounts: MappedDiscount[], lineItem) => {

    for (const pricePerQuantity of lineItem.discountedPricePerQuantity) {

      const quantity = pricePerQuantity.quantity;
      for (const includedDiscount of pricePerQuantity.discountedPrice
        .includedDiscounts) {

        const discountedAmount =
          (includedDiscount.discountedAmount?.centAmount * quantity) / 100;
        const existingDiscount = currentCartDiscounts.find(
          (cartDiscount) => cartDiscount.id === includedDiscount?.discount?.id,
        );

        if (existingDiscount) {

          existingDiscount.discountedAmount += discountedAmount;
          existingDiscount.products.push({
            name: lineItem.name || '',
            discountedAmount,
          });
        
        } else {

          const newDiscount = mapCartDiscount(includedDiscount, quantity);

          newDiscount.products.push({
            name: lineItem.name || '',
            discountedAmount,
          });
          currentCartDiscounts.push(newDiscount);
        
        }
      
      }
    
    }

    return currentCartDiscounts;
  
  },
  [],
);
